import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Headphones, Info, Loader2, Mic, Play, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import AgentPageShell from '../../components/agent/AgentPageShell';
import WaveformBars from '../../components/agent/WaveformBars';
import { cn } from '../../lib/utils';
import { fetchVoices, saveConfig } from '../../lib/agentApi';

const SPEAKING_STYLES = ['Natural', 'News', 'Conversational', 'Cheerful', 'Calm'];

const VoiceSelectionPage = () => (
  <AgentPageShell
    title="Voice Selection"
    description="Voice persona, accent and playback settings for this agent"
    icon={Mic}
    breadcrumb={['Voice Selection']}
  >
    {({ agent, orgId }) => <VoiceBody key={agent.id} agent={agent} orgId={orgId} />}
  </AgentPageShell>
);

const VoiceBody = ({ agent, orgId }) => {
  const [catalog, setCatalog] = useState([]);
  const [voice, setVoice] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [testPhrase, setTestPhrase] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchVoices(orgId, agent.id);
      setCatalog(result.voices || []);
      setVoice(result.selected || {});
      setStats(result.stats || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load voices');
    } finally {
      setLoading(false);
    }
  }, [orgId, agent.id]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (patch) => setVoice((v) => ({ ...v, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveConfig(orgId, agent.id, { voice_config: voice });
      toast.success('Voice configuration saved to the draft — publish to make it live');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading voices…
      </div>
    );
  }

  const arabic = catalog.filter((v) => v.language === 'ar');
  const english = catalog.filter((v) => v.language === 'en');
  const current = catalog.find((v) => v.id === voice.voice_id) || catalog[0] || {};

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" onClick={() => setPlaying((p) => !p)}>
          <Play className="w-4 h-4 mr-2 fill-current" />
          {playing ? 'Stop preview' : 'Preview voice'}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Configuration
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Current voice */}
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background relative overflow-hidden">
            <CardContent className="p-6 relative z-10">
              <Mic className="absolute top-2 right-4 w-28 h-28 opacity-[0.04] pointer-events-none" />
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex flex-col items-center gap-3 shrink-0">
                  <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center border-4 border-background shadow-xl ring-2 ring-primary/20">
                    <Headphones className="h-9 w-9 text-primary" />
                  </div>
                  <Badge className="bg-primary/20 text-primary border-primary/30">
                    {voice.enabled === false ? 'Voice off' : 'Voice on'}
                  </Badge>
                </div>
                <div className="flex-1 text-center md:text-left space-y-4 min-w-0">
                  <div>
                    <h2 className="text-xl font-bold">
                      {current.name} — {current.accent}
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      {current.description}. Used for every spoken reply from this agent.
                    </p>
                  </div>
                  <div className="flex items-center justify-center md:justify-start">
                    <WaveformBars count={24} active={playing} className="h-10 w-48" />
                  </div>
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                    <Button size="sm" className="rounded-full px-6" onClick={() => setPlaying((p) => !p)}>
                      <Play className="w-4 h-4 mr-2 fill-current" />
                      {playing ? 'Stop' : 'Play sample'}
                    </Button>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Voice replies</Label>
                      <Switch
                        checked={voice.enabled !== false}
                        onCheckedChange={(v) => set({ enabled: v })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Voice catalog */}
          <Tabs defaultValue="arabic">
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="arabic" className="px-6">
                  Arabic voices
                </TabsTrigger>
                <TabsTrigger value="english" className="px-6">
                  English voices
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="arabic" className="mt-0">
              <VoiceGrid
                voices={arabic}
                selectedId={voice.voice_id}
                onSelect={(id) => set({ voice_id: id })}
              />
            </TabsContent>
            <TabsContent value="english" className="mt-0">
              <VoiceGrid
                voices={english}
                selectedId={voice.voice_id}
                onSelect={(id) => set({ voice_id: id })}
              />
            </TabsContent>
          </Tabs>

          {/* Pronunciation tester */}
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="p-6 space-y-5">
              <h3 className="font-semibold">Pronunciation Tester</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Type a phrase to test… e.g. أهلاً بك في نظامنا الذكي"
                  value={testPhrase}
                  onChange={(e) => setTestPhrase(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={() => setPlaying(true)} disabled={!testPhrase.trim()}>
                  Generate audio
                </Button>
              </div>
              <WaveformBars count={32} active={playing} className="h-12" />
              <p className="text-[11px] text-muted-foreground">
                Text-to-speech is not wired to a provider yet — this previews the voice surface
                and settings only.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Settings */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h3 className="font-semibold">Voice Settings</h3>

              <SliderRow
                label="Playback speed"
                value={voice.speed ?? 1}
                display={`${(voice.speed ?? 1).toFixed(2)}x`}
                min={0.75}
                max={2}
                step={0.05}
                onChange={(v) => set({ speed: v })}
              />
              <SliderRow
                label="Pitch adjustment"
                value={voice.pitch ?? 0}
                display={String(voice.pitch ?? 0)}
                min={-10}
                max={10}
                step={1}
                onChange={(v) => set({ pitch: v })}
              />

              <div className="space-y-4 pt-2 border-t">
                <ToggleRow
                  label="Volume normalization"
                  hint="Keep levels consistent between replies"
                  checked={voice.volume_normalization !== false}
                  onChange={(v) => set({ volume_normalization: v })}
                />
                <ToggleRow
                  label="Noise cancellation"
                  hint="Filter background noise during capture"
                  checked={voice.noise_cancellation !== false}
                  onChange={(v) => set({ noise_cancellation: v })}
                />
                <ToggleRow
                  label="Auto language detection"
                  hint="Switch accent based on what the user speaks"
                  checked={voice.auto_language_detection !== false}
                  onChange={(v) => set({ auto_language_detection: v })}
                />
                <ToggleRow
                  label="SSML support"
                  hint="Speech Synthesis Markup Language"
                  checked={Boolean(voice.ssml)}
                  onChange={(v) => set({ ssml: v })}
                />
              </div>

              <div className="space-y-2 pt-2 border-t">
                <Label className="text-sm">Speaking style</Label>
                <div className="grid grid-cols-2 gap-2">
                  {SPEAKING_STYLES.map((style) => (
                    <button
                      key={style}
                      onClick={() => set({ speaking_style: style })}
                      className={cn(
                        'px-3 py-2 text-xs rounded-md border transition-all',
                        (voice.speaking_style || 'Natural') === style
                          ? 'bg-primary/10 border-primary text-primary font-medium'
                          : 'bg-background border-border hover:border-primary/40'
                      )}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Fallback voice</Label>
                <Select
                  value={voice.fallback_voice_id || 'aria'}
                  onValueChange={(v) => set({ fallback_voice_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {catalog.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} — {v.accent}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-sm">Voice Usage</h3>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  Voice sessions
                </span>
                <span className="text-2xl font-bold">{stats.voice_sessions ?? 0}</span>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Voice conversations cost noticeably more than text — speech-to-text on the way
                in, synthesis on every reply.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-primary/5 border-primary/10">
            <CardContent className="p-4 flex gap-3">
              <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Arabic dialects</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Dialect coverage (Gulf, Egyptian, Levantine, Darija, Saudi) depends on the
                  speech provider you connect. Pick the accent closest to your users.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const VoiceGrid = ({ voices, selectedId, onSelect }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {voices.map((voice) => {
      const isSelected = voice.id === selectedId;
      return (
        <Card
          key={voice.id}
          onClick={() => onSelect(voice.id)}
          className={cn(
            'relative cursor-pointer transition-all border-2',
            isSelected
              ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
              : 'border-border hover:border-primary/50'
          )}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={cn(
                    'p-2 rounded-full shrink-0',
                    isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  )}
                >
                  <Mic className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-sm truncate">{voice.name}</h4>
                  <p className="text-xs text-muted-foreground truncate">{voice.accent}</p>
                </div>
              </div>
              {isSelected && (
                <Badge className="bg-primary shrink-0">
                  <Check className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <WaveformBars count={8} active={isSelected} className="h-6 w-20 justify-start" />
              <span className="text-[11px] text-muted-foreground capitalize">{voice.gender}</span>
            </div>
          </CardContent>
        </Card>
      );
    })}
  </div>
);

const SliderRow = ({ label, value, display, min, max, step, onChange }) => (
  <div className="space-y-3">
    <div className="flex justify-between items-center">
      <Label className="text-sm">{label}</Label>
      <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">{display}</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} />
  </div>
);

const ToggleRow = ({ label, hint, checked, onChange }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="space-y-0.5 min-w-0">
      <Label className="text-sm">{label}</Label>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

export default VoiceSelectionPage;
